import React, { useState } from 'react';
import {
  Container,
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Collapse,
} from '@mui/material';

// UI-based JSON designer (Option A):
// - Basic Info (BaseProductUrl, is3DProduct, DesignUrl, FontUrl)
// - Product (ProductName, product.id, store.id)
// - Options (SupportedModels.Options)
// - Views (list of id/en/cn)
// - 3D Models (CommonProperties["3DMODEL"])
// - Themes (BackgroundsCsv, StickersCsv)
// - Variants (price, id, Option1/2/3)
// JSON is generated internally and shown as read-only preview.

const createEmptyView = () => ({ id: 'VIEW', en: 'VIEW', cn: 'VIEW' });
const createEmptyVariant = () => ({ price: '1', id: '', Option1: '', Option2: '', Option3: '' });
const createEmptyOption = () => ({
  key: 'Option1',
  name: '',
  displayMethod: '',
  displayNameEn: '',
  displayNameCn: '',
  isVisualOption: false,
  isSKU: false,
  notesLabelEn: '',
  notesLabelCn: '',
  notesContentEn: '',
  notesContentCn: '',
  values: [
    { value: '', en: '', cn: '' },
  ],
});

function JsonDesigner() {
  const [baseProductUrl, setBaseProductUrl] = useState('');
  const [is3DProduct, setIs3DProduct] = useState(false);
  const [guided, setGuided] = useState(false);
  const [designUrl, setDesignUrl] = useState('');
  const [fontUrl, setFontUrl] = useState('');

  const [productName, setProductName] = useState('');
  const [productId, setProductId] = useState('');
  const [storeId, setStoreId] = useState('');
  const [haveCase, setHaveCase] = useState(false);

  const [views, setViews] = useState([createEmptyView()]);
  const [variants, setVariants] = useState([createEmptyVariant()]);
  const [models3D, setModels3D] = useState([]); // CommonProperties.3DMODEL URLs

  const [backgroundsCsv, setBackgroundsCsv] = useState([]); // Themes[0].BackgroundsCsv
  const [stickersCsv, setStickersCsv] = useState([]); // Themes[0].StickersCsv

  const [hintColor, setHintColor] = useState('');
  const [hintPattern, setHintPattern] = useState(''); // e.g. 'dotLine45'
  const [hintPatternColor, setHintPatternColor] = useState('');

  const [options, setOptions] = useState([]);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(0);
  const [optionDialogOpen, setOptionDialogOpen] = useState(false);

  const [fileName, setFileName] = useState('product.json');
  const [jsonPreview, setJsonPreview] = useState('');
  const [error, setError] = useState('');

  // Collapsible sections (default collapsed)
  const [openProductSection, setOpenProductSection] = useState(false);
  const [openThemesSection, setOpenThemesSection] = useState(false);
  const [openOptionsSection, setOpenOptionsSection] = useState(false);
  const [openViewsSection, setOpenViewsSection] = useState(false);
  const [openVariantsSection, setOpenVariantsSection] = useState(false);

  const populateFromObject = (obj) => {
    try {
      setBaseProductUrl(obj.BaseProductUrl || '');
      setIs3DProduct(!!obj.is3DProduct);
      setHaveCase(!!obj.haveCase);
      setGuided(!!obj.Guided);
      setDesignUrl(obj.DesignUrl || '');
      setFontUrl(obj.FontUrl || '');
      setHintColor(obj.HintLayer?.color || '');
      setHintPattern(obj.HintLayer?.pattern || '');
      setHintPatternColor(obj.HintLayer?.patternColor || '');

      const sm = obj.SupportedModels || {};
      setProductName(sm.ProductName || '');

      const cp = sm.CommonProperties || {};
      const loadedViews = Array.isArray(cp.Views)
        ? cp.Views.map((v) => ({
            id: typeof v.id === 'string' ? v.id : 'VIEW',
            en: v.name?.en || v.id || 'VIEW',
            cn: v.name?.cn || v.id || 'VIEW',
          }))
        : [createEmptyView()];
      setViews(loadedViews.length ? loadedViews : [createEmptyView()]);

      const loaded3D = Array.isArray(cp['3DMODEL']) ? cp['3DMODEL'] : [];
      setModels3D(loaded3D);

      const themesArr = Array.isArray(obj.Themes) ? obj.Themes : [];
      const firstTheme = themesArr[0] || {};
      const loadedBackgrounds = Array.isArray(firstTheme.BackgroundsCsv)
        ? firstTheme.BackgroundsCsv.map((item) => ({
            Name: item.Name || '',
            Url: item.Url || '',
          }))
        : [];
      const loadedStickers = Array.isArray(firstTheme.StickersCsv)
        ? firstTheme.StickersCsv.map((item) => ({
            Name: item.Name || '',
            Url: item.Url || '',
          }))
        : [];
      setBackgroundsCsv(loadedBackgrounds);
      setStickersCsv(loadedStickers);

      const loadedVariants = Array.isArray(obj.variants)
        ? obj.variants.map((v) => ({
            price: String(v.price || '1'),
            id: v.id || '',
            Option1: v.Option1 || '',
            Option2: v.Option2 || '',
            Option3: v.Option3 || '',
          }))
        : [createEmptyVariant()];
      setVariants(loadedVariants.length ? loadedVariants : [createEmptyVariant()]);

      setProductId(obj.product?.id || '');
      setStoreId(obj.store?.id || '');

      const opts = sm.Options || {};
      const optionEntries = Object.entries(opts);
      const optList = optionEntries.map(([key, val]) => {
        const values = Array.isArray(val.values)
          ? val.values.map((vv) => ({
              value: vv.value || '',
              en: vv.displayName?.en || vv.value || '',
              cn: vv.displayName?.cn || vv.value || '',
            }))
          : [{ value: '', en: '', cn: '' }];
        return {
          key,
          name: val.name || '',
          displayMethod: val.displayMethod || '',
          displayNameEn:
            (val.displayName && (val.displayName.en || val.displayName.cn)) || '',
          displayNameCn:
            (val.displayName && (val.displayName.cn || val.displayName.en)) || '',
          isVisualOption: !!val.isVisualOption,
          isSKU: !!val.isSKU,
          notesLabelEn: val.Notes?.label?.en || '',
          notesLabelCn: val.Notes?.label?.cn || '',
          notesContentEn: val.Notes?.content?.en || '',
          notesContentCn: val.Notes?.content?.cn || '',
          values,
        };
      });
      setOptions(optList);
      setSelectedOptionIndex(0);
    } catch (e) {
      setError(`Failed to import JSON: ${e.message || String(e)}`);
    }
  };

  const handleLoadFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const text = String(evt.target?.result || '');
        const obj = JSON.parse(text);
        populateFromObject(obj);
        setError('');
      } catch (err) {
        setError('Failed to parse JSON file: ' + (err.message || String(err)));
      }
    };
    reader.onerror = () => {
      setError('Failed to read file');
    };
    reader.readAsText(file);
  };

  const updateViewField = (index, field, value) => {
    setViews((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addView = () => setViews((prev) => [...prev, createEmptyView()]);
  const removeView = (index) => setViews((prev) => prev.filter((_, i) => i !== index));

  const updateVariantField = (index, field, value) => {
    setVariants((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addVariant = () => setVariants((prev) => [...prev, createEmptyVariant()]);
  const removeVariant = (index) => setVariants((prev) => prev.filter((_, i) => i !== index));

  const addModel3D = () => {
    setModels3D((prev) => [...prev, '']);
  };

  const updateModel3D = (index, value) => {
    setModels3D((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const removeModel3D = (index) => {
    setModels3D((prev) => prev.filter((_, i) => i !== index));
  };

  const addBackground = () => {
    setBackgroundsCsv((prev) => [...prev, { Name: '', Url: '' }]);
  };

  const updateBackgroundField = (index, field, value) => {
    setBackgroundsCsv((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const removeBackground = (index) => {
    setBackgroundsCsv((prev) => prev.filter((_, i) => i !== index));
  };

  const addSticker = () => {
    setStickersCsv((prev) => [...prev, { Name: '', Url: '' }]);
  };

  const updateStickerField = (index, field, value) => {
    setStickersCsv((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const removeSticker = (index) => {
    setStickersCsv((prev) => prev.filter((_, i) => i !== index));
  };

  const addOption = () => {
    setOptions((prev) => {
      const nextKey = `Option${prev.length + 1}`;
      const next = [...prev, { ...createEmptyOption(), key: nextKey }];
      setSelectedOptionIndex(next.length - 1);
      return next;
    });
    setOptionDialogOpen(true);
  };

  const removeOption = (index) => {
    setOptions((prev) => {
      const next = prev.filter((_, i) => i !== index);
      let nextIndex = selectedOptionIndex;
      if (nextIndex >= next.length) nextIndex = next.length - 1;
      if (nextIndex < 0) nextIndex = 0;
      setSelectedOptionIndex(nextIndex);
      return next;
    });
  };

  const updateOptionField = (index, field, value) => {
    setOptions((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addOptionValue = (optIndex) => {
    setOptions((prev) => {
      const next = [...prev];
      const opt = next[optIndex];
      const values = Array.isArray(opt.values) ? opt.values.slice() : [];
      values.push({ value: '', en: '', cn: '' });
      next[optIndex] = { ...opt, values };
      return next;
    });
  };

  const updateOptionValueField = (optIndex, valIndex, field, value) => {
    setOptions((prev) => {
      const next = [...prev];
      const opt = next[optIndex];
      const values = Array.isArray(opt.values) ? opt.values.slice() : [];
      if (!values[valIndex]) values[valIndex] = { value: '', en: '', cn: '' };
      values[valIndex] = { ...values[valIndex], [field]: value };
      next[optIndex] = { ...opt, values };
      return next;
    });
  };

  const removeOptionValue = (optIndex, valIndex) => {
    setOptions((prev) => {
      const next = [...prev];
      const opt = next[optIndex];
      const values = (opt.values || []).filter((_, i) => i !== valIndex);
      next[optIndex] = { ...opt, values: values.length ? values : [{ value: '', en: '', cn: '' }] };
      return next;
    });
  };

  const buildJson = () => {
    setError('');
    try {
      if (!baseProductUrl.trim()) throw new Error('BaseProductUrl is required');

      const viewsBlock = views
        .filter((v) => v.id.trim())
        .map((v) => ({
          id: v.id.trim(),
          name: {
            en: v.en.trim() || v.id.trim(),
            cn: v.cn.trim() || v.id.trim(),
          },
        }));

      const variantsBlock = variants
        .filter((v) => v.id.trim())
        .map((v) => ({
          price: String(v.price || '1'),
          id: v.id.trim(),
          ...(v.Option1 ? { Option1: v.Option1 } : {}),
          ...(v.Option2 ? { Option2: v.Option2 } : {}),
          ...(v.Option3 ? { Option3: v.Option3 } : {}),
        }));

      const models3DBlock = is3DProduct
        ? models3D
            .map((u) => String(u || '').trim())
            .filter((u) => u.length > 0)
        : [];

      const backgroundsBlock = backgroundsCsv
        .map((b) => ({
          Name: (b.Name || '').trim(),
          Url: (b.Url || '').trim(),
        }))
        .filter((b) => b.Url.length > 0);

      const stickersBlock = stickersCsv
        .map((s) => ({
          Name: (s.Name || '').trim(),
          Url: (s.Url || '').trim(),
        }))
        .filter((s) => s.Url.length > 0);

      // Build Options object from UI
      const optionsObj = {};
      options.forEach((opt) => {
        const key = (opt.key || '').trim();
        if (!key) return;
        const valuesArr = (opt.values || [])
          .filter((v) => (v.value || '').trim())
          .map((v) => ({
            value: (v.value || '').trim(),
            displayName: {
              en: (v.en || '').trim() || (v.value || '').trim(),
              cn: (v.cn || '').trim() || (v.value || '').trim(),
            },
          }));
        optionsObj[key] = {
          name: (opt.name || '').trim(),
          ...(opt.displayMethod ? { displayMethod: opt.displayMethod } : {}),
          ...(opt.notesLabelEn || opt.notesLabelCn || opt.notesContentEn || opt.notesContentCn
            ? {
                Notes: {
                  label: {
                    en: opt.notesLabelEn || '',
                    cn: opt.notesLabelCn || '',
                  },
                  content: {
                    en: opt.notesContentEn || '',
                    cn: opt.notesContentCn || '',
                  },
                },
              }
            : {}),
          isVisualOption: !!opt.isVisualOption,
          isSKU: !!opt.isSKU,
          displayName: {
            en: (opt.displayNameEn || '').trim() || (opt.name || '').trim() || key,
            cn: (opt.displayNameCn || '').trim() || (opt.name || '').trim() || key,
          },
          values: valuesArr,
        };
      });

      const obj = {
        BaseProductUrl: baseProductUrl.trim(),
        is3DProduct,
        haveCase,
        Guided: guided,
        DesignUrl: designUrl.trim(),
        FontUrl: fontUrl.trim(),
        Themes:
          backgroundsBlock.length || stickersBlock.length
            ? [
                {
                  ...(backgroundsBlock.length
                    ? { BackgroundsCsv: backgroundsBlock }
                    : {}),
                  ...(stickersBlock.length ? { StickersCsv: stickersBlock } : {}),
                },
              ]
            : [],
        ExportedImagesPanel: {
          mode: 'DeliverPrint',
        },
        HintLayer: {
          color: hintColor.trim(),
          opacity: 1,
          ...(hintPattern ? { pattern: hintPattern } : {}),
          ...(hintPatternColor.trim()
            ? { patternColor: hintPatternColor.trim() }
            : {}),
        },
        style: {
          ProductTab: 'DeliverPrint',
        },
        SupportedModels: {
          ProductName: productName.trim(),
          Options: optionsObj,
          CommonProperties: {
            Views: viewsBlock,
            "3DMODEL": models3DBlock,
          },
        },
        variants: variantsBlock,
        product: {
          id: productId.trim(),
        },
        store: {
          id: storeId.trim(),
        },
      };

      const json = JSON.stringify(obj, null, 2);
      setJsonPreview(json);
      return obj;
    } catch (e) {
      setError(e.message || String(e));
      return null;
    }
  };

  const handleDownload = () => {
    const obj = buildJson();
    if (!obj) return;
    const json = JSON.stringify(obj, null, 2);
    try {
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName && fileName.trim() ? fileName.trim() : 'product.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || String(e));
    }
  };

  const handleRefreshPreview = () => {
    buildJson();
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Product JSON Designer
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Fill in the form below. The tool will generate the product config
            JSON for you to download and use in the Product Designer.
          </Typography>
          {error && (
            <Typography
              variant="body2"
              color="error"
              sx={{ mt: 1 }}
            >
              {error}
            </Typography>
          )}
        </Box>

        {/* Import JSON */}
        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Import Existing JSON
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Load an existing product config (e.g. gd.json, Poker.json) to pre-fill
            this form.
          </Typography>
          <Button variant="outlined" component="label" size="small">
            Choose JSON File
            <input
              type="file"
              accept="application/json,.json"
              hidden
              onChange={handleLoadFile}
            />
          </Button>
        </Paper>

        {/* Product Settings & Hint (Basic Info + Hint + Product / Store) */}
        <Paper elevation={1} sx={{ p: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ cursor: 'pointer' }}
            onClick={() => setOpenProductSection((v) => !v)}
          >
            <Typography variant="h6">Product Settings & Hint</Typography>
            <Typography variant="body2" color="text.secondary">
              {openProductSection ? 'Collapse' : 'Expand'}
            </Typography>
          </Stack>
          <Collapse in={openProductSection} timeout="auto" unmountOnExit>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
              Base product setup for this design (URL, 3D, case, guided mode),
              hint overlay style, and Shopify product/store IDs.
            </Typography>

            {/* Basic Info */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12}>
                <TextField
                  label="BaseProductUrl"
                  value={baseProductUrl}
                  onChange={(e) => setBaseProductUrl(e.target.value)}
                  fullWidth
                  placeholder="https://.../DeliverPrint/Products/..."
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={is3DProduct}
                      onChange={(e) => setIs3DProduct(e.target.checked)}
                    />
                  }
                  label="is3DProduct"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={guided}
                      onChange={(e) => setGuided(e.target.checked)}
                    />
                  }
                  label="Guided"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={haveCase}
                      onChange={(e) => setHaveCase(e.target.checked)}
                    />
                  }
                  label="haveCase (load Case.png next to product)"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="DesignUrl"
                  value={designUrl}
                  onChange={(e) => setDesignUrl(e.target.value)}
                  fullWidth
                />
              </Grid>
            </Grid>

            {/* Hint Layer */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Hint overlay (color and 45° pattern).
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Hint color"
                  value={hintColor}
                  onChange={(e) => setHintColor(e.target.value)}
                  fullWidth
                  placeholder="rgba(0,0,0,0.5) or #00000080"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Dot line color"
                  value={hintPatternColor}
                  onChange={(e) => setHintPatternColor(e.target.value)}
                  fullWidth
                  placeholder="#FFFFFF or rgba(...)"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  label="Pattern"
                  value={hintPattern}
                  onChange={(e) => setHintPattern(e.target.value)}
                  fullWidth
                  helperText="For future use; engine must support this pattern."
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="dotLine45">Dot line 45°</MenuItem>
                </TextField>
              </Grid>
            </Grid>

            {/* Product / Store */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Shopify product/store IDs.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="SupportedModels.ProductName"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="product.id"
                  value={productId}
                  onChange={(e) => setProductId(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="store.id"
                  value={storeId}
                  onChange={(e) => setStoreId(e.target.value)}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Collapse>
        </Paper>

        {/* 3D Models */}
        {is3DProduct && (
          <Paper elevation={1} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              3D Models (CommonProperties["3DMODEL"])
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Add one URL per 3D model file (e.g. Card, Box). Leave empty if not
              used.
            </Typography>
            <Stack spacing={1}>
              {models3D.map((url, idx) => (
                <Stack
                  key={idx}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                >
                  <TextField
                    label={`Model URL ${idx + 1}`}
                    size="small"
                    value={url}
                    onChange={(e) => updateModel3D(idx, e.target.value)}
                    fullWidth
                    placeholder="https://.../3DMODEL/.../model.fbx"
                  />
                  <Button
                    size="small"
                    color="error"
                    onClick={() => removeModel3D(idx)}
                  >
                    Remove
                  </Button>
                </Stack>
              ))}
              <Button size="small" variant="outlined" onClick={addModel3D}>
                Add 3D Model URL
              </Button>
            </Stack>
          </Paper>
        )}

        {/* Themes: BackgroundsCsv & StickersCsv plus FontUrl */}
        <Paper elevation={1} sx={{ p: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ cursor: 'pointer' }}
            onClick={() => setOpenThemesSection((v) => !v)}
          >
            <Typography variant="h6">Themes & Font</Typography>
            <Typography variant="body2" color="text.secondary">
              {openThemesSection ? 'Collapse' : 'Expand'}
            </Typography>
          </Stack>
          <Collapse in={openThemesSection} timeout="auto" unmountOnExit>
            <Grid container spacing={2} sx={{ mb: 2, mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="FontUrl"
                  value={fontUrl}
                  onChange={(e) => setFontUrl(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: { xs: 1, sm: 1.5 } }}
                >
                  Configure BackgroundsCsv and StickersCsv entries for the first
                  theme.
                </Typography>
              </Grid>
            </Grid>

            <Box sx={{ mb: 2 }}>
              <Stack direction="row" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle1">BackgroundsCsv</Typography>
                <Button size="small" variant="outlined" onClick={addBackground}>
                  Add Background
                </Button>
              </Stack>
              {backgroundsCsv.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No backgrounds yet.
                </Typography>
              )}
              {backgroundsCsv.length > 0 && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Url</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {backgroundsCsv.map((b, idx) => (
                      <TableRow key={idx}>
                        <TableCell width="25%">
                          <TextField
                            size="small"
                            value={b.Name}
                            onChange={(e) =>
                              updateBackgroundField(idx, 'Name', e.target.value)
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={b.Url}
                            onChange={(e) =>
                              updateBackgroundField(idx, 'Url', e.target.value)
                            }
                            fullWidth
                          />
                        </TableCell>
                        <TableCell align="center" width="10%">
                          <Button
                            size="small"
                            color="error"
                            onClick={() => removeBackground(idx)}
                          >
                            Remove
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>

            <Box>
              <Stack direction="row" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle1">StickersCsv</Typography>
                <Button size="small" variant="outlined" onClick={addSticker}>
                  Add Sticker Set
                </Button>
              </Stack>
              {stickersCsv.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No sticker sets yet.
                </Typography>
              )}
              {stickersCsv.length > 0 && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Url</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stickersCsv.map((s, idx) => (
                      <TableRow key={idx}>
                        <TableCell width="25%">
                          <TextField
                            size="small"
                            value={s.Name}
                            onChange={(e) =>
                              updateStickerField(idx, 'Name', e.target.value)
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={s.Url}
                            onChange={(e) =>
                              updateStickerField(idx, 'Url', e.target.value)
                            }
                            fullWidth
                          />
                        </TableCell>
                        <TableCell align="center" width="10%">
                          <Button
                            size="small"
                            color="error"
                            onClick={() => removeSticker(idx)}
                          >
                            Remove
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </Paper>

        {/* Options */}
        <Paper elevation={1} sx={{ p: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ cursor: 'pointer' }}
            onClick={() => setOpenOptionsSection((v) => !v)}
          >
            <Typography variant="h6">Options</Typography>
            <Typography variant="body2" color="text.secondary">
              {openOptionsSection ? 'Collapse' : 'Expand'}
            </Typography>
          </Stack>
          <Collapse in={openOptionsSection} timeout="auto" unmountOnExit>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, mt: 1 }}>
              Define SupportedModels.Options here. Each row is one option object
              (Option1, Option2, ...). Click Edit to open a form card and fill
              its data and values.
            </Typography>
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1">Option List</Typography>
                <Button size="small" variant="outlined" onClick={addOption}>
                  Add Option
                </Button>
              </Stack>
              {options.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No options yet. Click "Add Option" to create one.
                </Typography>
              )}
              {options.length > 0 && (
                <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Key</TableCell>
                    <TableCell>name</TableCell>
                    <TableCell>displayName EN</TableCell>
                    <TableCell>displayName CN</TableCell>
                    <TableCell>displayMethod</TableCell>
                    <TableCell>isVisual</TableCell>
                    <TableCell>isSKU</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {options.map((opt, idx) => (
                    <TableRow
                      key={idx}
                      hover
                    >
                      <TableCell width="10%">
                        <TextField
                          size="small"
                          value={opt.key}
                          onChange={(e) => updateOptionField(idx, 'key', e.target.value)}
                        />
                      </TableCell>
                      <TableCell width="15%">
                        <TextField
                          size="small"
                          value={opt.name}
                          onChange={(e) => updateOptionField(idx, 'name', e.target.value)}
                        />
                      </TableCell>
                      <TableCell width="15%">
                        <TextField
                          size="small"
                          value={opt.displayNameEn}
                          onChange={(e) =>
                            updateOptionField(idx, 'displayNameEn', e.target.value)
                          }
                        />
                      </TableCell>
                      <TableCell width="15%">
                        <TextField
                          size="small"
                          value={opt.displayNameCn}
                          onChange={(e) =>
                            updateOptionField(idx, 'displayNameCn', e.target.value)
                          }
                        />
                      </TableCell>
                      <TableCell width="20%">
                        <TextField
                          size="small"
                          placeholder="verticalRadioButton..."
                          value={opt.displayMethod}
                          onChange={(e) =>
                            updateOptionField(idx, 'displayMethod', e.target.value)
                          }
                        />
                      </TableCell>
                      <TableCell width="7%">
                        <Checkbox
                          size="small"
                          checked={opt.isVisualOption}
                          onChange={(e) =>
                            updateOptionField(idx, 'isVisualOption', e.target.checked)
                          }
                        />
                      </TableCell>
                      <TableCell width="7%">
                        <Checkbox
                          size="small"
                          checked={opt.isSKU}
                          onChange={(e) =>
                            updateOptionField(idx, 'isSKU', e.target.checked)
                          }
                        />
                      </TableCell>
                      <TableCell align="center" width="11%">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedOptionIndex(idx);
                              setOptionDialogOpen(true);
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeOption(idx);
                            }}
                          >
                            Delete
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            </Stack>
          </Collapse>
        </Paper>

        {/* Views */}
        <Paper elevation={1} sx={{ p: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            mb={1}
            sx={{ cursor: 'pointer' }}
            onClick={() => setOpenViewsSection((v) => !v)}
          >
            <Typography variant="h6">Views</Typography>
            <Typography variant="body2" color="text.secondary">
              {openViewsSection ? 'Collapse' : 'Expand'}
            </Typography>
          </Stack>
          <Collapse in={openViewsSection} timeout="auto" unmountOnExit>
            <Stack direction="row" justifyContent="space-between" mb={1}>
              <Button size="small" variant="outlined" onClick={addView}>
                Add View
              </Button>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Define all views for this product (e.g. FRONT / BACK, CARD / BOX).
            </Typography>
            <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name EN</TableCell>
                <TableCell>Name CN</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {views.map((v, idx) => (
                <TableRow key={idx}>
                  <TableCell width="20%">
                    <TextField
                      size="small"
                      value={v.id}
                      onChange={(e) =>
                        updateViewField(idx, 'id', e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell width="30%">
                    <TextField
                      size="small"
                      value={v.en}
                      onChange={(e) =>
                        updateViewField(idx, 'en', e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell width="30%">
                    <TextField
                      size="small"
                      value={v.cn}
                      onChange={(e) =>
                        updateViewField(idx, 'cn', e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell align="center" width="20%">
                    <Button
                      size="small"
                      color="error"
                      disabled={views.length <= 1}
                      onClick={() => removeView(idx)}
                    >
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </Collapse>
        </Paper>

        {/* Variants */}
        <Paper elevation={1} sx={{ p: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            mb={1}
            sx={{ cursor: 'pointer' }}
            onClick={() => setOpenVariantsSection((v) => !v)}
          >
            <Typography variant="h6">Variants</Typography>
            <Typography variant="body2" color="text.secondary">
              {openVariantsSection ? 'Collapse' : 'Expand'}
            </Typography>
          </Stack>
          <Collapse in={openVariantsSection} timeout="auto" unmountOnExit>
            <Stack direction="row" justifyContent="space-between" mb={1}>
              <Button size="small" variant="outlined" onClick={addVariant}>
                Add Variant
              </Button>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Map Shopify variant IDs to options. Option1/Option2/Option3 are free
              text (e.g. Printing / Embroidery, DTG / DTF, Paper Stock).
            </Typography>
            <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Price</TableCell>
                <TableCell>Variant ID</TableCell>
                <TableCell>Option1</TableCell>
                <TableCell>Option2</TableCell>
                <TableCell>Option3</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {variants.map((v, idx) => (
                <TableRow key={idx}>
                  <TableCell width="10%">
                    <TextField
                      size="small"
                      value={v.price}
                      onChange={(e) =>
                        updateVariantField(idx, 'price', e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell width="25%">
                    <TextField
                      size="small"
                      value={v.id}
                      onChange={(e) =>
                        updateVariantField(idx, 'id', e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell width="25%">
                    <TextField
                      size="small"
                      placeholder="Printing / Embroidery"
                      value={v.Option1}
                      onChange={(e) =>
                        updateVariantField(idx, 'Option1', e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell width="20%">
                    <TextField
                      size="small"
                      placeholder="DTG / DTF"
                      value={v.Option2}
                      onChange={(e) =>
                        updateVariantField(idx, 'Option2', e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell width="20%">
                    <TextField
                      size="small"
                      placeholder="Stock / Material"
                      value={v.Option3}
                      onChange={(e) =>
                        updateVariantField(idx, 'Option3', e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell align="center" width="10%">
                    <Button
                      size="small"
                      color="error"
                      disabled={variants.length <= 1}
                      onClick={() => removeVariant(idx)}
                    >
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </Collapse>
        </Paper>

        {/* Output */}
        <Paper elevation={1} sx={{ p: 2, mb: 6 }}>
          <Typography variant="h6" gutterBottom>
            Output
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            sx={{ mb: 1 }}
          >
            <TextField
              label="File name"
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
              sx={{ width: { xs: '100%', sm: 220 } }}
              placeholder="product.json"
              size="small"
            />
            <Box flexGrow={1} />
            <Button
              variant="outlined"
              size="small"
              onClick={handleRefreshPreview}
            >
              Refresh Preview
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleDownload}
            >
              Download JSON
            </Button>
          </Stack>
          <TextField
            value={jsonPreview}
            multiline
            minRows={10}
            fullWidth
            InputProps={{ readOnly: true }}
            sx={{ fontFamily: 'monospace', fontSize: 12 }}
          />
        </Paper>

        {/* Option Edit Dialog */}
        <Dialog
          open={optionDialogOpen && !!options[selectedOptionIndex]}
          fullWidth
          maxWidth="md"
          onClose={() => setOptionDialogOpen(false)}
        >
          {options[selectedOptionIndex] && (
            <>
              <DialogTitle>
                Edit Option {options[selectedOptionIndex].key || ''}
              </DialogTitle>
              <DialogContent dividers>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Basic Info
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        size="small"
                        label="Key"
                        value={options[selectedOptionIndex].key}
                        onChange={(e) =>
                          updateOptionField(selectedOptionIndex, 'key', e.target.value)
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        size="small"
                        label="name"
                        value={options[selectedOptionIndex].name}
                        onChange={(e) =>
                          updateOptionField(selectedOptionIndex, 'name', e.target.value)
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        size="small"
                        label="displayMethod"
                        placeholder="verticalRadioButton, horizontalButtonGroup"
                        value={options[selectedOptionIndex].displayMethod}
                        onChange={(e) =>
                          updateOptionField(
                            selectedOptionIndex,
                            'displayMethod',
                            e.target.value,
                          )
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        label="displayName EN"
                        value={options[selectedOptionIndex].displayNameEn}
                        onChange={(e) =>
                          updateOptionField(
                            selectedOptionIndex,
                            'displayNameEn',
                            e.target.value,
                          )
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        label="displayName CN"
                        value={options[selectedOptionIndex].displayNameCn}
                        onChange={(e) =>
                          updateOptionField(
                            selectedOptionIndex,
                            'displayNameCn',
                            e.target.value,
                          )
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Stack direction="row" spacing={2}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={options[selectedOptionIndex].isVisualOption}
                              onChange={(e) =>
                                updateOptionField(
                                  selectedOptionIndex,
                                  'isVisualOption',
                                  e.target.checked,
                                )
                              }
                            />
                          }
                          label="isVisualOption"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={options[selectedOptionIndex].isSKU}
                              onChange={(e) =>
                                updateOptionField(
                                  selectedOptionIndex,
                                  'isSKU',
                                  e.target.checked,
                                )
                              }
                            />
                          }
                          label="isSKU"
                        />
                      </Stack>
                    </Grid>
                  </Grid>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Notes
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        label="Notes label EN"
                        value={options[selectedOptionIndex].notesLabelEn}
                        onChange={(e) =>
                          updateOptionField(
                            selectedOptionIndex,
                            'notesLabelEn',
                            e.target.value,
                          )
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        label="Notes label CN"
                        value={options[selectedOptionIndex].notesLabelCn}
                        onChange={(e) =>
                          updateOptionField(
                            selectedOptionIndex,
                            'notesLabelCn',
                            e.target.value,
                          )
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        label="Notes content EN"
                        value={options[selectedOptionIndex].notesContentEn}
                        onChange={(e) =>
                          updateOptionField(
                            selectedOptionIndex,
                            'notesContentEn',
                            e.target.value,
                          )
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        label="Notes content CN"
                        value={options[selectedOptionIndex].notesContentCn}
                        onChange={(e) =>
                          updateOptionField(
                            selectedOptionIndex,
                            'notesContentCn',
                            e.target.value,
                          )
                        }
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Box>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="subtitle2">Values</Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => addOptionValue(selectedOptionIndex)}
                    >
                      Add Value
                    </Button>
                  </Stack>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>value</TableCell>
                        <TableCell>displayName EN</TableCell>
                        <TableCell>displayName CN</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(options[selectedOptionIndex].values || []).map((val, vIdx) => (
                        <TableRow key={vIdx}>
                          <TableCell width="20%">
                            <TextField
                              size="small"
                              value={val.value}
                              onChange={(e) =>
                                updateOptionValueField(
                                  selectedOptionIndex,
                                  vIdx,
                                  'value',
                                  e.target.value,
                                )
                              }
                            />
                          </TableCell>
                          <TableCell width="30%">
                            <TextField
                              size="small"
                              value={val.en}
                              onChange={(e) =>
                                updateOptionValueField(
                                  selectedOptionIndex,
                                  vIdx,
                                  'en',
                                  e.target.value,
                                )
                              }
                            />
                          </TableCell>
                          <TableCell width="30%">
                            <TextField
                              size="small"
                              value={val.cn}
                              onChange={(e) =>
                                updateOptionValueField(
                                  selectedOptionIndex,
                                  vIdx,
                                  'cn',
                                  e.target.value,
                                )
                              }
                            />
                          </TableCell>
                          <TableCell align="center" width="20%">
                            <Button
                              size="small"
                              color="error"
                              onClick={() =>
                                removeOptionValue(selectedOptionIndex, vIdx)
                              }
                            >
                              Remove
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOptionDialogOpen(false)}>Close</Button>
                <Button
                  variant="contained"
                  onClick={() => setOptionDialogOpen(false)}
                >
                  Save
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Stack>
    </Container>
  );
}

export default JsonDesigner;
